import React, { useEffect, useState } from 'react';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Grid,
  IconButton,
  LinearProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { ArrowBack as ArrowBackIcon, Edit as EditIcon, SummarizeOutlined as SummarizeIcon } from '@mui/icons-material';
import { NameNicknameDisplay, TimeDisplay } from '../components/common';
import CouponValidityPeriod from '../components/coupon/CouponValidityPeriod';
import CouponEditDialog from '../components/coupon/CouponEditDialog';
import { getCouponUsages } from '../api/coupons';
import { Coupon, CouponUsage } from '../types/coupon';
import { PATIENT_THEME } from '../types/patient';
import { NURSE_TYPE_THEME } from '../types/nurse';
import { formatDate } from '../utils/dateUtils';

const CouponUsageList: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [ coupon, setCoupon ] = useState<Coupon | null>(null);
  const [ couponUsages, setCouponUsages ] = useState<CouponUsage[]>([]);
  const [ totalCount, setTotalCount ] = useState(0);
  const [ page, setPage ] = useState(0);
  const [ rowsPerPage, setRowsPerPage ] = useState(20);
  const [ loading, setLoading ] = useState(true);
  const [ error, setError ] = useState<string | null>(null);
  const [ openEditDialog, setOpenEditDialog ] = useState(false);
  const fetchCouponUsages = async() => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const couponId = parseInt(id, 10);
      if (isNaN(couponId)) {
        throw new Error('無効なクーポンIDです');
      }
      const params = {
        page: page + 1, // APIは1-indexed
        page_size: rowsPerPage,
      };
      const data = await getCouponUsages(couponId, params);
      setCouponUsages(data.results);
      setTotalCount(data.count);
      setCoupon(data.coupon);  // クーポン基本情報をセット
    } catch (err) {
      console.error('クーポン利用履歴の取得に失敗しました', err);
      setError('クーポン利用履歴の取得に失敗しました。');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    fetchCouponUsages();
  }, [ id, page, rowsPerPage ]);
  const handleBack = () => {
    navigate('/coupons');
  };
  const handlePageChange = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };
  const handleRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };
  const handleOpenEditDialog = () => {
    setOpenEditDialog(true);
  };
  const handleCloseEditDialog = () => {
    setOpenEditDialog(false);
  };
  const handleEditSuccess = () => {
    // 編集成功後、データを再取得
    fetchCouponUsages();
    handleCloseEditDialog();
  };
  const getStatusChip = () => {
    if (!coupon) return null;
    const chipStyle = {
      fontWeight: 'bold',
      minWidth: '80px',
      maxWidth: '100%',
    };
    if (coupon.disabled) {
      return <Chip label="無効" size="small" color="error" sx={chipStyle}/>;
    }
    if (coupon.is_active) {
      return <Chip label="期間中" size="small" color="success" sx={chipStyle}/>;
    }
    const today = new Date();
    const startDate = new Date(coupon.start_date);
    if (today < startDate) {
      return <Chip label="開始前" size="small" color="warning" sx={chipStyle}/>;
    }
    if (coupon.end_date) {
      const endDate = new Date(coupon.end_date);
      if (today > endDate) {
        return <Chip label="終了" size="small" color="default" sx={chipStyle}/>;
      }
    }
    return <Chip label="不明" size="small" sx={chipStyle}/>;
  };
  if (loading) {
    return (
      <Box sx={{ my: 4, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <LinearProgress sx={{ width: '100%', mb: 2 }}/>
        <Typography variant="body1">クーポンデータを読み込み中...</Typography>
      </Box>
    );
  }
  if (error || !coupon) {
    return (
      <Box sx={{ my: 4, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <Alert severity="error">{error || 'クーポンデータの読み込みに失敗しました。'}</Alert>
        <Button startIcon={<ArrowBackIcon/>} sx={{ mt: 2 }} onClick={handleBack}>
          一覧に戻る
        </Button>
      </Box>
    );
  }
  return (
    <Box>
      {/* 戻るボタン */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'flex-start', alignItems: 'center' }}>
        <Button onClick={handleBack} startIcon={<ArrowBackIcon/>} variant="outlined" color="primary">
          クーポン一覧に戻る
        </Button>
      </Box>

      {/* クーポン基本情報 */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              {getStatusChip()}
              <Typography variant="h5" component="h1">
                {coupon.name}
              </Typography>
            </Box>
            <IconButton
              size="medium"
              onClick={handleOpenEditDialog}
              sx={{
                backgroundColor: 'rgba(25, 118, 210, 0.08)',
                color: 'primary.main',
                '&:hover': {
                  backgroundColor: 'rgba(25, 118, 210, 0.12)',
                  color: 'primary.main',
                }
              }}
            >
              <EditIcon/>
            </IconButton>
          </Box>

          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="subtitle2" color="text.secondary">
                クーポンコード
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 'bold', fontSize: '1.1em' }}>
                {coupon.code}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="subtitle2" color="text.secondary">
                割引
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                {coupon.discount_type === 'FIXED' ? (
                  <>
                    {coupon.discount_value.toLocaleString()} <span style={{ fontWeight: 'bold' }}>円</span>
                  </>
                ) : coupon.discount_type === 'RATE' ? (
                  <>
                    {coupon.discount_value} <span style={{ fontWeight: 'bold' }}>%</span>
                  </>
                ) : (
                  coupon.discount_display
                )}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="subtitle2" color="text.secondary">
                有効期間
              </Typography>
              <CouponValidityPeriod coupon={coupon}/>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="subtitle2" color="text.secondary">
                利用上限
              </Typography>
              <Typography variant="body1">
                {coupon.usage_limit ? (
                  <>{coupon.usage_limit} 回</>
                ) : (
                  <>なし</>
                )}
              </Typography>
            </Grid>
            {coupon.remarks && (
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary">
                  備考
                </Typography>
                <Typography variant="body1">{coupon.remarks}</Typography>
              </Grid>
            )}
          </Grid>
        </CardContent>
      </Card>

      {/* 利用履歴 */}
      <Typography variant="h6" sx={{ mb: 2, textAlign: 'center' }}>
        利用履歴 （ {coupon.usage_limit ? (
        <>
          <span style={{ fontWeight: 'bold', fontSize: '1.3em' }}>{totalCount}</span> / {coupon.usage_limit} 回
        </>
      ) : (
        <>
          <span style={{ fontWeight: 'bold', fontSize: '1.1em' }}>{totalCount}</span> 回
        </>
      )} ）
      </Typography>
      {couponUsages.length === 0 && !loading ? (
        <Paper elevation={2} sx={{ p: 3, textAlign: 'center' }}>
          <Typography color="text.secondary">まだ利用されていません</Typography>
        </Paper>
      ) : (
        <Paper elevation={2}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>利用日時</TableCell>
                  <TableCell>利用者(患者)</TableCell>
                  <TableCell>対話相手(看護師)</TableCell>
                  <TableCell align="right" sx={{ width: '160px' }}>申込みプラン</TableCell>
                  <TableCell align="right">割引額</TableCell>
                  <TableCell>備考</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {couponUsages.map((usage) => (
                  <TableRow key={usage.id}>
                    <TableCell>
                      <TimeDisplay timestamp={usage.used_at} tooltipPrefix="利用日時" fontSize="0.875rem"/>
                    </TableCell>
                    <TableCell
                      sx={{
                        padding: 0,
                        cursor: 'pointer'
                      }}
                    >
                      <Tooltip
                        title={
                          <Typography variant="body2" sx={{ fontWeight: 'bold' }}>患者詳細ページへ</Typography>
                        }
                        arrow
                        placement="top"
                      >
                        <Box
                          component={RouterLink}
                          to={`/patients/${usage.patient.id}`}
                          sx={{
                            textDecoration: 'none',
                            color: 'inherit',
                            display: 'block',
                            width: '100%',
                            height: '100%',
                            padding: '16px'
                          }}
                        >
                          <Box sx={{
                            display: 'flex',
                            alignItems: 'center',
                            borderRadius: '4px',
                            transition: 'all 0.2s ease',
                            p: 0.5,
                            ml: -0.5,
                            '&:hover': {
                              backgroundColor: 'rgba(76, 175, 80, 0.08)',
                              '& .patient-name': {
                                color: '#4CAF50'
                              }
                            }
                          }}>
                            <Box className="patient-name" sx={{ transition: 'color 0.2s ease' }}>
                              <NameNicknameDisplay
                                name={usage.patient.name}
                                nickname={usage.patient.nickname}
                                enableHoverEffect={false}
                                nameFontSize="0.875rem"
                                nameFontWeight="bold"
                                nameColor={PATIENT_THEME.MYSELF.DARK}
                                nicknameFontSize="0.8rem"
                                nicknameValueFontWeight="bold"
                              />
                            </Box>
                          </Box>
                        </Box>
                      </Tooltip>
                    </TableCell>
                    <TableCell
                      sx={{
                        padding: usage.talk_session && usage.talk_session.nurse ? 0 : undefined,
                        cursor: usage.talk_session && usage.talk_session.nurse ? 'pointer' : 'default'
                      }}
                    >
                      {usage.talk_session && usage.talk_session.nurse ? (
                        <Tooltip
                          title={
                            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>看護師詳細ページへ</Typography>
                          }
                          arrow
                          placement="top"
                        >
                          <Box
                            component={RouterLink}
                            to={`/nurses/${usage.talk_session.nurse.id}`}
                            sx={{
                              textDecoration: 'none',
                              color: 'inherit',
                              display: 'block',
                              width: '100%',
                              height: '100%',
                              padding: '16px'
                            }}
                          >
                            <Box sx={{
                              display: 'flex',
                              alignItems: 'center',
                              borderRadius: '4px',
                              transition: 'all 0.2s ease',
                              p: 0.5,
                              ml: -0.5,
                              '&:hover': {
                                backgroundColor: 'rgba(245, 0, 87, 0.08)',
                                '& .nurse-name': {
                                  color: '#f50057'
                                }
                              }
                            }}>
                              <Box className="nurse-name" sx={{ transition: 'color 0.2s ease' }}>
                                <NameNicknameDisplay
                                  name={usage.talk_session.nurse.name}
                                  nickname={usage.talk_session.nurse.nickname}
                                  enableHoverEffect={false}
                                  nameFontSize="0.875rem"
                                  nameFontWeight="bold"
                                  nameColor={NURSE_TYPE_THEME.NORMAL.DARK}
                                  nicknameFontSize="0.8rem"
                                  nicknameValueFontWeight="bold"
                                />
                              </Box>
                            </Box>
                          </Box>
                        </Tooltip>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          -
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{
                        padding: usage.talk_session ? 0 : undefined,
                        cursor: usage.talk_session ? 'pointer' : 'default'
                      }}
                    >
                      {usage.talk_session ? (
                        <Tooltip
                          title={
                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: 'bold' }}>対話情報ページへ</Typography>
                              <Typography variant="body2">
                                期間: {usage.talk_session.start_at ? formatDate(usage.talk_session.start_at) : '-'} 〜 {usage.talk_session.end_at ? formatDate(usage.talk_session.end_at) : '-'}
                              </Typography>
                            </Box>
                          }
                          arrow
                          placement="top"
                        >
                          <Box
                            component={RouterLink}
                            to={`/talk_rooms/summary?nurse_id=${usage.talk_session.parent_id}&patient_id=${usage.talk_session.child_id}`}
                            style={{
                              textDecoration: 'none',
                              color: 'inherit',
                              display: 'block',
                              width: '100%',
                              height: '100%',
                              padding: '16px'
                            }}
                          >
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'flex-end',
                                borderRadius: '4px',
                                transition: 'all 0.2s ease',
                                p: 0.5,
                                pr: 1,
                                '&:hover': {
                                  backgroundColor: 'rgba(25, 118, 210, 0.08)',
                                  '& .room-label': {
                                    color: 'primary.main',
                                  },
                                  '& .summary-icon': {
                                    opacity: 1
                                  }
                                }
                              }}
                            >
                              <Typography
                                variant="body2"
                                fontWeight="medium"
                                className="room-label"
                                sx={{ transition: 'color 0.2s ease' }}
                              >
                                ¥{usage.talk_plan_price.toLocaleString()}
                              </Typography>
                              <SummarizeIcon
                                fontSize="small"
                                sx={{
                                  ml: 0.5,
                                  opacity: 0.7,
                                  transition: 'opacity 0.2s ease'
                                }}
                                color="primary"
                                className="summary-icon"
                              />
                            </Box>
                          </Box>
                        </Tooltip>
                      ) : (
                        <Box sx={{ textAlign: 'right' }}>¥{usage.talk_plan_price.toLocaleString()}</Box>
                      )}
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                      ¥{usage.discount_amount.toLocaleString()}
                    </TableCell>
                    <TableCell>
                      {usage.remarks ? (
                        <Typography variant="body2">{usage.remarks}</Typography>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          -
                        </Typography>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[ 10, 20, 50 ]}
            component="div"
            count={totalCount}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handlePageChange}
            onRowsPerPageChange={handleRowsPerPageChange}
            labelRowsPerPage="表示件数:"
            labelDisplayedRows={({ from, to, count }) =>
              `${count}件中 ${from}-${to}件`
            }
          />
        </Paper>
      )}

      {coupon && (
        <CouponEditDialog
          open={openEditDialog}
          onClose={handleCloseEditDialog}
          coupon={coupon}
          onSuccess={handleEditSuccess}
        />
      )}
    </Box>
  );
};
export default CouponUsageList;
